import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBook, FaChartBar, FaBaby, FaCalendarAlt, FaHandPointUp, FaStar, FaPhone, FaLightbulb } from 'react-icons/fa';
import SectionHeader from './common/SectionHeader';
import SubsectionHeader from './common/SubsectionHeader';
import { api } from '../services/api';
import ConfirmationModal from './ConfirmationModal';
import { useFeedbackModal } from './common/useFeedbackModal';
import './KickCounter.css';

function KickCounter({ userId }) {
  const [activeSession, setActiveSession] = useState(null);
  const [kickCount, setKickCount] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [duration, setDuration] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [currentWeek, setCurrentWeek] = useState(null);
  const [showEducation, setShowEducation] = useState(true);
  const [expandedEducation, setExpandedEducation] = useState(new Set());
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [activeHistoryTab, setActiveHistoryTab] = useState('today');
  const { modal, openModal, closeModal } = useFeedbackModal();

  const loadPregnancyWeek = useCallback(async () => {
    try {
      const profile = await api.getPregnancyProfile(userId).catch(() => null);
      if (profile?.lmpDate) {
        const lmpDate = new Date(profile.lmpDate);
        const today = new Date();
        const diffTime = today - lmpDate;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const weeks = Math.floor(diffDays / 7);
        setCurrentWeek(weeks);
      }
    } catch (error) {
      console.error('Error loading pregnancy week:', error);
    }
  }, [userId]);

  const loadSessions = useCallback(async () => {
    try {
      const data = await api.getKickSessions(userId);
      setSessions(data);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadSessions();
      loadPregnancyWeek();
    }
  }, [userId, loadSessions, loadPregnancyWeek]);

  useEffect(() => {
    let interval = null;
    if (activeSession && startTime) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setDuration(elapsed);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeSession, startTime]);


  const startSession = async () => {
    try {
      setLoading(true);
      const session = await api.startKickSession(userId);
      setActiveSession(session);
      setKickCount(0);
      setStartTime(Date.now());
      setDuration(0);
      setNotes('');
    } catch (error) {
      console.error('Error starting session:', error);
      openModal({
        title: 'Start failed',
        message: 'Failed to start session.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const addKick = async () => {
    if (!activeSession) return;
    
    try {
      const updated = await api.addKick(activeSession.id, userId);
      setKickCount(updated.kickCount);
      setActiveSession(updated);
    } catch (error) {
      console.error('Error adding kick:', error);
    }
  };

  const endSession = async () => {
    if (!activeSession) return;
    
    try {
      setLoading(true);
      // Ensure userId is a string
      const userIdStr = String(userId);
      await api.endKickSession(activeSession.id, userIdStr, notes || '');
      
      // Create a creative success message based on kick count
      let message = '';
      if (kickCount >= 10) {
        message = {
          title: '🎉 Amazing Session!',
          body: `You tracked ${kickCount} movements! Your baby is active and healthy. This beautiful moment has been saved to your history. Keep nurturing that wonderful connection with your little one! 💕`,
          icon: '🎉',
          color: 'success'
        };
      } else if (kickCount >= 5) {
        message = {
          title: '✨ Great Job!',
          body: `You recorded ${kickCount} movements. Every movement is precious and shows your baby is doing well. This session has been saved! 👶`,
          icon: '✨',
          color: 'success'
        };
      } else if (kickCount > 0) {
        message = {
          title: '💖 Beautiful Moments',
          body: `You tracked ${kickCount} ${kickCount === 1 ? 'movement' : 'movements'}. Your baby's movements are special moments to cherish. Session saved! 🌟`,
          icon: '💖',
          color: 'info'
        };
      } else {
        message = {
          title: '💕 Session Saved',
          body: `Don't worry if you didn't feel movements yet - babies have quiet periods too. Try again later, especially after meals or when you're relaxed. You're doing great! ✨`,
          icon: '💕',
          color: 'info'
        };
      }
      
      // Show success message
      setSuccessMessage(message);
      setShowSuccessMessage(true);
      
      setActiveSession(null);
      setKickCount(0);
      setStartTime(null);
      setDuration(0);
      setNotes('');
      loadSessions();
    } catch (error) {
      console.error('Error ending session:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to end session';
      openModal({
        title: 'End failed',
        message: `Failed to end session: ${errorMessage}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleEducation = (key, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setExpandedEducation(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Educational content based on ACOG and Mayo Clinic guidelines
  const educationContent = [
    {
      id: 'when-to-start',
      title: 'When to Start Counting',
      icon: <FaCalendarAlt />,
      color: '#E91E63',
      preview: 'Kick counting typically begins around 28 weeks of pregnancy, when fetal movements become more predictable.',
      content: (
        <div className="education-content-wrapper">
          <p className="education-content-intro"><strong>According to ACOG guidelines:</strong></p>
          <p className="education-content-text">
            Most healthcare providers recommend starting kick counting around <strong>28 weeks of pregnancy</strong>. 
            By this time, your baby's movements are more regular and easier to track.
          </p>
          <p className="education-content-text">
            However, if you're at high risk or your healthcare provider recommends it, you may start earlier. 
            Always follow your healthcare provider's specific recommendations for your pregnancy.
          </p>
          <p className="education-content-source">
            Source: ACOG Committee Opinion on Fetal Movement Counting
          </p>
        </div>
      )
    },
    {
      id: 'how-to-count',
      title: 'How to Count Kicks',
      icon: <FaHandPointUp />,
      color: '#EC407A',
      preview: 'Find a comfortable position, focus on your baby, and count each distinct movement you feel.',
      content: (
        <div className="education-content-wrapper">
          <p className="education-content-intro"><strong>The "Count to 10" Method (ACOG Recommended):</strong></p>
          <ol className="education-content-list">
            <li>Choose a time when your baby is usually active (often after meals or in the evening)</li>
            <li>Find a comfortable position, preferably lying on your left side</li>
            <li>Start counting each distinct movement you feel (kicks, rolls, jabs, swishes)</li>
            <li>Count until you reach 10 movements</li>
            <li>Note how long it took to reach 10 movements</li>
          </ol>
          <p className="education-content-text">
            <strong>What counts as a movement?</strong> Any distinct movement you feel - kicks, rolls, jabs, swishes, 
            or any other type of movement. You don't need to count every tiny flutter, just distinct movements.
          </p>
          <p className="education-content-source">
            Source: Mayo Clinic Pregnancy Guide, ACOG Guidelines
          </p>
        </div>
      )
    },
    {
      id: 'whats-normal',
      title: 'What\'s Normal?',
      icon: <FaStar />,
      color: '#F48FB1',
      preview: 'Most babies reach 10 movements within 1-2 hours. Every baby has their own pattern, so get to know yours!',
      content: (
        <div className="education-content-wrapper">
          <p className="education-content-intro"><strong>Normal Movement Patterns:</strong></p>
          <ul className="education-content-list">
            <li>
              <strong>Most babies</strong> reach 10 movements within <strong>1-2 hours</strong>
            </li>
            <li>
              Some active babies may reach 10 movements in <strong>15-30 minutes</strong>
            </li>
            <li>
              It's normal for babies to have <strong>quieter periods</strong> when they're sleeping
            </li>
            <li>
              Babies often have <strong>active times</strong> (usually after meals, in the evening, or when you're resting)
            </li>
            <li>
              Movement patterns can vary from day to day - <strong>what matters is knowing your baby's normal pattern</strong>
            </li>
          </ul>
          <p className="education-content-text">
            <strong>Remember:</strong> Every baby is unique! The most important thing is to get to know your baby's 
            individual movement pattern. If your baby is consistently active and you notice a significant change, 
            that's when to pay attention.
          </p>
          <p className="education-content-source">
            Source: Mayo Clinic, ACOG Guidelines
          </p>
        </div>
      )
    },
    {
      id: 'when-to-call',
      title: 'When to Call Your Doctor',
      icon: <FaPhone />,
      color: '#DC3545',
      preview: 'If you notice decreased movement or it takes more than 2 hours to feel 10 movements, contact your healthcare provider immediately.',
      content: (
        <div className="education-content-wrapper">
          <p className="education-content-intro"><strong>Call your healthcare provider immediately if:</strong></p>
          <ul className="education-content-list">
            <li>
              <strong>You feel fewer than 10 movements in 2 hours</strong> (after trying the counting method)
            </li>
            <li>
              <strong>Your baby's movements have significantly decreased</strong> from their normal pattern
            </li>
            <li>
              <strong>You notice no movement at all</strong> for several hours (especially after 28 weeks)
            </li>
            <li>
              <strong>You're concerned</strong> about any change in your baby's movement pattern
            </li>
          </ul>
          <p className="education-content-text">
            <strong>Important:</strong> Don't wait until the next day. If you're concerned about decreased fetal movement, 
            contact your healthcare provider right away. They can check on your baby's well-being.
          </p>
          <div className="education-tip-box">
            <strong>💡 Trust your instincts:</strong> You know your baby better than anyone. If something feels off, 
            don't hesitate to reach out to your healthcare provider. It's always better to be safe and get reassurance.
          </div>
          <p className="education-content-source">
            Source: ACOG Committee Opinion, Mayo Clinic Emergency Guidelines
          </p>
        </div>
      )
    },
    {
      id: 'tips',
      title: 'Helpful Tips',
      icon: <FaLightbulb />,
      color: '#FF9800',
      preview: 'Try counting after meals, drink something cold, or gently nudge your belly to encourage movement.',
      content: (
        <div className="education-content-wrapper">
          <p className="education-content-intro"><strong>Tips to Encourage Movement:</strong></p>
          <ul className="education-content-list">
            <li>
              <strong>Count after meals:</strong> Babies are often more active after you eat
            </li>
            <li>
              <strong>Drink something cold:</strong> The temperature change can sometimes wake your baby
            </li>
            <li>
              <strong>Lie on your left side:</strong> This position improves blood flow and may make movements more noticeable
            </li>
            <li>
              <strong>Gently nudge your belly:</strong> Sometimes a gentle touch can encourage movement
            </li>
            <li>
              <strong>Choose a quiet time:</strong> When you're relaxed and focused, you're more likely to notice movements
            </li>
          </ul>
          <p className="education-content-text">
            <strong>Remember:</strong> If you try these tips and still don't feel adequate movement, 
            don't wait - contact your healthcare provider.
          </p>
          <p className="education-content-source">
            Source: Mayo Clinic Pregnancy Guide
          </p>
        </div>
      )
    }
  ];

  return (
    <div className="kick-counter-container">
      {/* Success Message Modal */}
      <AnimatePresence>
        {showSuccessMessage && successMessage && (
          <motion.div
            className="success-message-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSuccessMessage(false)}
          >
            <motion.div
              className={`success-message-card ${successMessage.color}`}
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                className="success-icon-large"
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, 10, -10, 0]
                }}
                transition={{ duration: 0.6, repeat: 1 }}
              >
                {successMessage.icon}
              </motion.div>
              <h3 className="success-title">{successMessage.title}</h3>
              <p className="success-body">{successMessage.body}</p>
              <motion.button
                className="success-close-button"
                onClick={() => setShowSuccessMessage(false)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Got it! 💕
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header Section */}
      <div className="kick-counter-content-wrapper">
        {/* Main Header */}
        <SectionHeader
          icon={<FaBaby className="section-icon" />}
          title="Baby's Movement Tracker"
          subtitle={(() => {
            if (!currentWeek) {
              return 'Track and celebrate every precious movement from your little one';
            }
            if (currentWeek >= 28) {
              return `You're ${currentWeek} weeks along - Perfect time to track your baby's movements!`;
            }
            if (currentWeek >= 20) {
              return `You're ${currentWeek} weeks along - You may start feeling movements soon!`;
            }
            return `You're ${currentWeek} weeks along - Your baby is growing beautifully!`;
          })()}
          isExpanded={true}
          onToggle={() => {}}
          showToggle={false}
          iconAnimation="float"
        />

        {/* Educational Content Header */}
        <SubsectionHeader
          icon={<FaBook className="subsection-icon" />}
          title="Learn About Kick Counting"
          subtitle="Understanding when and how to track your baby's movements"
          isExpanded={showEducation}
          onToggle={() => setShowEducation(!showEducation)}
          showToggle={true}
          iconAnimation="rotate"
        />

        {/* Educational Content */}
        <AnimatePresence>
          {showEducation && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="education-section-modern"
              style={{ marginTop: 'var(--spacing-md)' }}
            >
              <div className="education-grid-modern">
                {educationContent.map((item, index) => (
                  <EducationCard
                    key={item.id}
                    item={item}
                    index={index}
                    isExpanded={expandedEducation.has(item.id)}
                    onToggle={(e) => toggleEducation(item.id, e)}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Kick Counter Interface */}
        <div className="kick-counter-interface">
          {!activeSession ? (
            <div className="kick-start-section">
              <motion.div
                className="start-card-pink"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="start-icon-wrapper">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                  >
                    <div className="start-icon-large">👶</div>
                  </motion.div>
                </div>
                <h3 className="start-title">Ready to Feel Your Baby Move?</h3>
                <p className="start-description">
                  {(() => {
                    if (currentWeek && currentWeek < 28) {
                      return `You're ${currentWeek} weeks along. While kick counting typically starts around 28 weeks, you can still track any movements you feel! Every movement is special.`;
                    }
                    if (currentWeek && currentWeek >= 28) {
                      return `Perfect timing! You're ${currentWeek} weeks along - the ideal time to start tracking your baby's movements. This helps you get to know your baby's patterns and provides peace of mind.`;
                    }
                    return 'Start tracking your baby\'s precious movements. This helps you get to know your baby\'s unique patterns and provides reassurance throughout your pregnancy journey.';
                  })()}
                </p>
                {currentWeek && currentWeek < 20 && (
                  <div className="early-pregnancy-note">
                    <p>💕 <strong>Early in your journey?</strong> Don't worry if you don't feel movements yet. Most women start feeling their baby move between 18-25 weeks. Every pregnancy is unique!</p>
                  </div>
                )}
                <motion.button
                  className="start-session-button-pink"
                  onClick={startSession}
                  disabled={loading}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <>
                      <span className="button-spinner"></span>
                      <span>Starting...</span>
                    </>
                  ) : (
                    <>
                      <span>🎯</span>
                      <span>Start Tracking Session</span>
                    </>
                  )}
                </motion.button>
              </motion.div>
            </div>
          ) : (
            <div className="kick-active-section">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                {/* Stats Cards */}
                <div className="kick-stats-grid">
                  <motion.div
                    className="kick-stat-card primary"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    whileHover={{ scale: 1.05, y: -4 }}
                  >
                    <div className="stat-icon-large">👶</div>
                    <div className="stat-value-huge">{kickCount}</div>
                    <div className="stat-label-pink">Movements</div>
                    {kickCount >= 10 && (
                      <motion.div
                        className="milestone-badge"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 200 }}
                      >
                        ✨ 10+ Movements!
                      </motion.div>
                    )}
                  </motion.div>
                  <motion.div
                    className="kick-stat-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    whileHover={{ scale: 1.05, y: -4 }}
                  >
                    <div className="stat-icon">⏱️</div>
                    <div className="stat-value">{formatTime(duration)}</div>
                    <div className="stat-label-pink">Duration</div>
                  </motion.div>
                </div>

                {/* Kick Button */}
                <motion.button
                  className="kick-button-pink"
                  onClick={addKick}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  animate={{
                    boxShadow: kickCount > 0 
                      ? ['0 8px 30px rgba(233, 30, 99, 0.4)', '0 12px 40px rgba(233, 30, 99, 0.6)', '0 8px 30px rgba(233, 30, 99, 0.4)']
                      : '0 8px 30px rgba(233, 30, 99, 0.4)'
                  }}
                  transition={{ duration: 0.5, repeat: kickCount > 0 ? Infinity : 0 }}
                >
                  <motion.div
                    className="kick-icon-large"
                    animate={{ 
                      scale: [1, 1.2, 1],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{ 
                      duration: 0.6,
                      repeat: Infinity,
                      repeatDelay: 2
                    }}
                  >
                    👶
                  </motion.div>
                  <span className="kick-text-large">Tap When You Feel Movement</span>
                  <span className="kick-hint">Each tap = one distinct movement</span>
                </motion.button>

                {/* Notes Section */}
                <div className="kick-notes-section">
                  <label className="notes-label-pink">
                    <span className="notes-icon">📝</span>
                    Notes (Optional)
                  </label>
                  <textarea
                    className="notes-textarea-pink"
                    placeholder="How did your baby move? Any patterns you noticed? This is your special moment to remember..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows="3"
                  />
                </div>

                {/* End Session Button */}
                <motion.button
                  className="end-session-button-pink"
                  onClick={endSession}
                  disabled={loading}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <>
                      <span className="button-spinner"></span>
                      <span>Ending Session...</span>
                    </>
                  ) : (
                    <>
                      <span>✅</span>
                      <span>Complete Session</span>
                    </>
                  )}
                </motion.button>
              </motion.div>
            </div>
          )}
        </div>

        {/* History Section */}
        {sessions.length > 0 && (
          <div className="kick-history-section" style={{ marginTop: 'var(--spacing-xl)' }}>
            <SubsectionHeader
              icon={<FaChartBar className="subsection-icon" />}
              title="Your Movement History"
              subtitle={`${sessions.length} ${sessions.length === 1 ? 'session' : 'sessions'} recorded - Watch your baby's patterns emerge`}
              isExpanded={true}
              onToggle={() => {}}
              showToggle={false}
              iconAnimation="scale"
            />
            <div className="foods-container meal-tabs-container-pink" style={{ marginTop: 'var(--spacing-md)' }}>
              {/* Date-based Tabs */}
              <div className="meal-tabs-pink">
                {(() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const todayCount = sessions.filter(session => {
                    const sessionDate = new Date(session.sessionDate);
                    sessionDate.setHours(0, 0, 0, 0);
                    return sessionDate.getTime() === today.getTime();
                  }).length;
                  
                  const weekAgo = new Date(today);
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  const thisWeekCount = sessions.filter(session => {
                    const sessionDate = new Date(session.sessionDate);
                    sessionDate.setHours(0, 0, 0, 0);
                    return sessionDate >= weekAgo && sessionDate < today;
                  }).length;
                  
                  const monthAgo = new Date(today);
                  monthAgo.setDate(monthAgo.getDate() - 30);
                  const thisMonthCount = sessions.filter(session => {
                    const sessionDate = new Date(session.sessionDate);
                    sessionDate.setHours(0, 0, 0, 0);
                    return sessionDate >= monthAgo && sessionDate < weekAgo;
                  }).length;
                  
                  const olderCount = sessions.filter(session => {
                    const sessionDate = new Date(session.sessionDate);
                    sessionDate.setHours(0, 0, 0, 0);
                    return sessionDate < monthAgo;
                  }).length;
                  
                  const tabs = [
                    { key: 'today', label: 'Today', count: todayCount, icon: '📅' },
                    { key: 'week', label: 'This Week', count: thisWeekCount, icon: '📆' },
                    { key: 'month', label: 'This Month', count: thisMonthCount, icon: '🗓️' },
                    { key: 'older', label: 'Older', count: olderCount, icon: '📜' }
                  ];
                  
                  return tabs.map(tab => (
                    <button
                      key={tab.key}
                      className={`meal-tab-pink ${activeHistoryTab === tab.key ? 'active' : ''}`}
                      onClick={() => setActiveHistoryTab(tab.key)}
                      disabled={tab.count === 0}
                    >
                      <span className="tab-icon">{tab.icon}</span>
                      <span className="tab-label">{tab.label}</span>
                      {tab.count > 0 && (
                        <span className="tab-count">{tab.count}</span>
                      )}
                    </button>
                  ));
                })()}
              </div>
              
              <div className="meal-content-pink">
                <div className="meals-grid-pink">
                  {(() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    let filteredSessions = [];
                    
                    if (activeHistoryTab === 'today') {
                      filteredSessions = sessions.filter(session => {
                        const sessionDate = new Date(session.sessionDate);
                        sessionDate.setHours(0, 0, 0, 0);
                        return sessionDate.getTime() === today.getTime();
                      });
                    } else if (activeHistoryTab === 'week') {
                      const weekAgo = new Date(today);
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      filteredSessions = sessions.filter(session => {
                        const sessionDate = new Date(session.sessionDate);
                        sessionDate.setHours(0, 0, 0, 0);
                        return sessionDate >= weekAgo && sessionDate < today;
                      });
                    } else if (activeHistoryTab === 'month') {
                      const monthAgo = new Date(today);
                      monthAgo.setDate(monthAgo.getDate() - 30);
                      const weekAgo = new Date(today);
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      filteredSessions = sessions.filter(session => {
                        const sessionDate = new Date(session.sessionDate);
                        sessionDate.setHours(0, 0, 0, 0);
                        return sessionDate >= monthAgo && sessionDate < weekAgo;
                      });
                    } else if (activeHistoryTab === 'older') {
                      const monthAgo = new Date(today);
                      monthAgo.setDate(monthAgo.getDate() - 30);
                      filteredSessions = sessions.filter(session => {
                        const sessionDate = new Date(session.sessionDate);
                        sessionDate.setHours(0, 0, 0, 0);
                        return sessionDate < monthAgo;
                      });
                    }
                    
                    // Sort by date (newest first)
                    filteredSessions.sort((a, b) => new Date(b.sessionDate) - new Date(a.sessionDate));
                    
                    if (filteredSessions.length === 0) {
                      return (
                        <div className="empty-state" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 'var(--spacing-xl)', color: 'var(--text-secondary)' }}>
                          <p style={{ fontSize: '1.1rem', marginBottom: 'var(--spacing-sm)' }}>No sessions found</p>
                          <p style={{ fontSize: '0.9rem' }}>You don't have any movement tracking sessions for this time period.</p>
                        </div>
                      );
                    }
                    
                    return filteredSessions.map((session, index) => (
                      <HistorySessionCard key={session.id} session={session} index={index} />
                    ));
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={() => {}}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText="OK"
        cancelText=""
      />
    </div>
  );
}

function EducationCard({ item, index, isExpanded, onToggle }) {
  return (
    <motion.div
      className={`education-card-modern ${isExpanded ? 'expanded' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.3 }}
      whileHover={!isExpanded ? { scale: 1.02, y: -4 } : {}}
      style={{ '--card-color': item.color }}
    >
      <motion.div 
        className="education-card-header-modern" 
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle(e);
        }}
        whileHover={{ backgroundColor: 'rgba(255, 250, 252, 0.8)' }}
      >
        <div className="education-card-icon-wrapper">
          <motion.div
            className="education-card-icon"
            animate={isExpanded ? { rotate: 360, scale: 1.1 } : { rotate: 0, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            {item.icon}
          </motion.div>
        </div>
        <div className="education-card-content">
          <h4 className="education-card-title">{item.title}</h4>
          <p className="education-card-preview">{item.preview}</p>
        </div>
        <motion.button 
          className="education-expand-button" 
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onToggle(e);
          }}
        >
          <motion.span
            className={`education-expand-icon ${isExpanded ? 'expanded' : ''}`}
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            ▼
          </motion.span>
        </motion.button>
      </motion.div>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className="education-card-details-modern"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {item.content}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function HistorySessionCard({ session, index }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const dateFormatted = new Date(session.sessionDate).toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <motion.div
      className={`meal-card-pink history-session-card ${isExpanded ? 'expanded' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.3 }}
      whileHover={!isExpanded ? { scale: 1.02, y: -4 } : {}}
    >
      <div className="meal-card-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="meal-card-main">
          <div className="meal-card-title-section">
            <h4 className="meal-name-modern">📅 {dateFormatted}</h4>
            <p className="meal-description-short">
              {session.kickCount} {session.kickCount === 1 ? 'movement' : 'movements'}
              {session.durationMinutes && ` • ${session.durationMinutes} minutes`}
            </p>
          </div>
          <div className="meal-card-meta">
            {session.kickCount >= 10 && (
              <div className="meal-badge calories-badge badge-success">
                <span className="badge-icon">✨</span>
                <span className="badge-value">10+ Movements</span>
              </div>
            )}
          </div>
        </div>
        <button className="expand-button" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
        </button>
      </div>
      {isExpanded && (
        <div className="meal-card-details">
          <div className="detail-section">
            <h5 className="detail-title">
              <span className="detail-icon">👶</span>
              Movements Recorded
            </h5>
            <p className="recipe-text">
              <strong>{session.kickCount}</strong> {session.kickCount === 1 ? 'movement' : 'movements'} 
              {session.kickCount >= 10 && (
                <span style={{ marginLeft: '8px', color: 'var(--success-color)' }}>
                  ✨ Great! You reached the recommended 10 movements!
                </span>
              )}
            </p>
          </div>
          {session.durationMinutes && (
            <div className="detail-section">
              <h5 className="detail-title">
                <span className="detail-icon">⏱️</span>
                Session Duration
              </h5>
              <p className="recipe-text">{session.durationMinutes} minutes</p>
            </div>
          )}
          {session.notes && (
            <div className="detail-section">
              <h5 className="detail-title">
                <span className="detail-icon">📝</span>
                Your Notes
              </h5>
              <p className="recipe-text">{session.notes}</p>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

export default KickCounter;
